import { useTranslate } from '@refinedev/core';
import { Card, Col, Descriptions, Grid, Row, Typography, theme } from 'antd';
import type { DescriptionsProps } from 'antd';
import type { PublicationPremiereDetailOut } from '@/api/generated/schemas';
import { ShowIconLink } from '@/components/common/ShowIconLink';
import { PublicationIconLink } from '@/components/user_catalog/publications/PublicationIconLink';
import { orDash, useVerticalDescProps } from '@/components/common/show/shared';
import { ProductFormatTag } from '@/components/user_catalog/publications/ProductFormatTag';
import { personLine } from '@/components/user_catalog/publications/personLine';
import { publicationFormats } from '@/components/user_catalog/publications/publicationFacts';
import { RESOURCE } from '../constants';
import { PublicationPremiereFollowersCard } from './PublicationPremiereFollowersCard';

const { Text } = Typography;

interface PublicationPremiereGeneralTabProps {
  record: PublicationPremiereDetailOut;
}

export const PublicationPremiereGeneralTab = ({
  record,
}: PublicationPremiereGeneralTabProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();
  const verticalDescProps = useVerticalDescProps();
  const f = (key: string) => t(`${RESOURCE}.fields.${key}`);

  const formats = publicationFormats(record);
  const authors = personLine(
    record.publication_authors?.map((a) => ({
      position: a.position,
      name: a.author?.name,
    }))
  );
  const lectors = personLine(
    record.publication_lectors?.map((l) => ({
      position: l.position,
      name: l.lector?.name,
    }))
  );

  const items: DescriptionsProps['items'] = [
    {
      key: 'id',
      label: t('base.fields.id'),
      children: <Text code>{record.id}</Text>,
    },
    {
      key: 'title',
      label: f('title'),
      children: (
        <PublicationIconLink publicationId={record.id}>
          {record.title}
        </PublicationIconLink>
      ),
    },
    {
      key: 'slug',
      label: f('slug'),
      children: (
        <Text code copyable style={{ overflowWrap: 'anywhere' }}>
          {record.slug}
        </Text>
      ),
    },
    {
      key: 'publisher',
      label: f('publisher_id'),
      children: orDash(
        record.publisher && (
          <ShowIconLink
            resource="user_catalog.publishers"
            id={record.publisher.id}
          >
            {record.publisher.name}
          </ShowIconLink>
        )
      ),
    },
    {
      key: 'authors',
      label: t('resources.authors'),
      children: orDash(authors),
    },
    {
      key: 'lectors',
      label: t('resources.lectors'),
      children: orDash(lectors),
    },
    {
      key: 'formats',
      label: t('user_catalog.publications.fields.formats'),
      children: orDash(
        formats.length > 0 && (
          <>
            {formats.map((kind) => (
              <ProductFormatTag key={kind} kind={kind} />
            ))}
          </>
        )
      ),
    },
  ];

  return (
    // Followers are the view's main content, so they take the wide column; the
    // few basic facts sit in a narrow one beside them and wrap below on small
    // screens.
    <Row gutter={[token.margin, token.margin]}>
      <Col flex="1 1 480px">
        <PublicationPremiereFollowersCard
          publicationId={record.id}
          followersCount={record.followers_count}
        />
      </Col>
      <Col flex={screens.lg ? '0 1 420px' : '0 1 360px'}>
        <Card title={t('base.sections.basic_info')}>
          <Descriptions {...verticalDescProps} column={1} items={items} />
        </Card>
      </Col>
    </Row>
  );
};
